import { useGetUploadFileTokenMutation } from "@/shared/services";
import { getStoryDisplayTitle } from "@/shared/utils/story-display-title";
import type { WholeStoryType } from "@config";
import { environment } from "@environment";
import {
  bakeDateTokens,
  useCustomDateNames,
} from "@features/dynamic-variables/date-variable";
import {
  pruneOptionalEntities,
  pruneOptionalEntitiesForPreview,
} from "@features/dynamic-variables/make-optional/resolve";
import safeCompile, { escapeJsonControlChars } from "@lib/handlebars";
import { useCallback } from "react";
import {
  createStoryFileKey,
  renderStory,
  uploadFile,
} from "../rendering-provider/utils";
import { resolveSystemTokens } from "../rendering-provider/utils/resolve-system-tokens";

export const useGeneratePreviewLink = () => {
  const { mutateAsync: getUploadFileToken } = useGetUploadFileTokenMutation();
  // The org's user-created `date` variables. Needed so an offset/format on a
  // CUSTOM date variable (`{{contract_start+1y}}`) is baked here instead of
  // reaching Handlebars, whose sanitizer blanks a token containing `+`/`|`.
  const customDateNames = useCustomDateNames();
  const handlePreviewLink = useCallback(
    async (
      story: WholeStoryType,
      versionId: string | null | undefined,
      preview = true,
      versionData?: Record<string, unknown> | null,
    ) => {
      const filename = createStoryFileKey({ story, versionId, preview });

      const result = await getUploadFileToken({
        filename,
        contentType: "text/html",
        typeStory: true,
      });
      const url = `${environment.stories}/${result.url.fields.key}`;

      // For a version preview the slides still hold raw `{{variable}}`
      // templates — nothing on the backend resolves them for the static
      // preview HTML. Run the same `compileStory` used when opening a
      // version, but with Handlebars ON (skipHandlebars defaults to false),
      // so the reader sees the parsed values instead of the placeholders.
      // `compileStory` returns a fresh object, so the caller's story (and the
      // stored slides) keep their templates intact.

      const storyToRender =
        versionData && Object.keys(versionData).length > 0
          ? // Bake date tokens to literal values FIRST (their `+`/`|` chars
            // would be mangled by the Handlebars sanitizer), then resolve the
            // remaining `{{variable}}` templates, then drop optional entities
            // the sender excluded / a condition hides so they never reach the
            // shared HTML.
            // A preview's bundle has no `__SD_DATA__` to re-decide with, so
            // its survivors are settled here (see the helper).
            (preview ? pruneOptionalEntitiesForPreview : pruneOptionalEntities)(
              JSON.parse(
                safeCompile(
                  bakeDateTokens(
                    JSON.stringify(story),
                    versionData,
                    new Date(),
                    customDateNames,
                  ),
                  escapeJsonControlChars({
                    ...versionData,
                    system: versionData,
                  }) as Record<string, unknown>,
                ),
              ),
              versionData,
            )
          : story;

      const rendered = renderStory({ story: storyToRender, preview });
      // A preview never reaches the server that fills `{{system.*}}` on a
      // real version link, so its head kept raw `{{system.canonicalUrl}}` /
      // `{{system.ogImage}}`. Fill what a preview has; the rest (PDF link,
      // gate, analytics ids) blanks. A non-preview render IS baked by the
      // server later, so it keeps its tokens.
      const settings = story.settings;
      const doc = preview
        ? resolveSystemTokens(rendered, {
            canonicalUrl: url,
            ogImage: settings?.og?.image ?? "",
            title: getStoryDisplayTitle(settings) ?? "",
            pageTitle: settings?.title ?? "",
            versionTitle: getStoryDisplayTitle(settings) ?? "",
            versionId: versionId ?? "",
          })
        : rendered;
      const blob = new Blob([doc], { type: "text/html" });
      await uploadFile(result.url, blob);
      return url;
    },
    [getUploadFileToken, customDateNames],
  );

  return handlePreviewLink;
};
